import { Collapse } from "components/ui/collapse/Collapse"
import { MODEL_NUMBER_COLUMN_WIDTHS } from "./TableModelNumbers.constants"
import { useCollapseModelNumbers } from "components/pages/PageConfiguration/layout/ModelNumbers/layout/TableModelNumbers/TableModelNumbersCollapse.hook"
import { useTableModelNumbersHandles } from "components/pages/PageConfiguration/layout/ModelNumbers/layout/TableModelNumbers/TableModelNumbersHandles"
import { useGetModelNumbers } from "hooks/api/modelNumbers/modelNumbers.hook"
import { Skeleton } from "components/ui/skeleton/Skeleton"
import type { TModelNumbersData } from "types/modelNumbers.type"
import { InputSearch } from "components/ui/inputs/InputSearch"
import { useDebounce } from "hooks/debounce.hook"
import { countModelNumbers, filterModelNumbers } from "./modelNumbersSearch.helpers"
import { useMemo, useState } from "react"
import type { TOnChangeInput } from "types/default.type"
import style from "./TableModelNumbers.module.scss"

const { tableModelNumbers, noData, tableHeader, headerColumn, searchBlock, searchField, resultsHint } = style

const SEARCH_DEBOUNCE_MS = 300

const HEADER_COLUMNS = [
	{ title: "", width: MODEL_NUMBER_COLUMN_WIDTHS.modelNumber },
	{ title: "WIDTH", width: MODEL_NUMBER_COLUMN_WIDTHS.width },
	{ title: "HEIGHT", width: MODEL_NUMBER_COLUMN_WIDTHS.height },
	{ title: "DEPTH", width: MODEL_NUMBER_COLUMN_WIDTHS.depth },
	{ title: "MIN SIZE", width: MODEL_NUMBER_COLUMN_WIDTHS.minSize },
	{ title: "AVG SIZE", width: MODEL_NUMBER_COLUMN_WIDTHS.avgSize },
	{ title: "MAX SIZE", width: MODEL_NUMBER_COLUMN_WIDTHS.maxSize },
]

export const TableModelNumbers = () => {
	const { data: modelNumbers, isPending: isModelNumbersPending } = useGetModelNumbers()
	const { onRowHandle } = useTableModelNumbersHandles()
	const [searchTerm, setSearchTerm] = useState("")
	// The big category is virtualized, so rows are filtered in the data rather than in
	// the DOM. Debounced because a keystroke re-filters ~2400 model numbers.
	const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS)
	const isSearching = Boolean(debouncedSearchTerm.trim())

	const {
		data: visibleModelNumbers,
		matches,
		matchedTopLevelKeys,
	} = useMemo(
		() => filterModelNumbers(modelNumbers as TModelNumbersData | undefined, debouncedSearchTerm),
		[modelNumbers, debouncedSearchTerm],
	)
	const total = useMemo(() => countModelNumbers(modelNumbers as TModelNumbersData | undefined), [modelNumbers])

	const { generateCollapseItems } = useCollapseModelNumbers({
		onRowHandle,
		isSearching,
		searchTerm: debouncedSearchTerm,
	})

	// Without this, every render re-walks the whole category tree, re-counts it and
	// re-flattens ~2400 model numbers into fresh row objects, handing antd a new
	// dataSource identity and re-rendering all rows.
	const collapseItems = useMemo(
		() => generateCollapseItems(visibleModelNumbers as TModelNumbersData),
		[generateCollapseItems, visibleModelNumbers],
	)

	if (isModelNumbersPending) {
		return (
			<div className={tableModelNumbers}>
				<div data-testid="skeleton">
					<Skeleton rows={10} />
				</div>
			</div>
		)
	}

	return (
		<div className={tableModelNumbers}>
			<div className={searchBlock}>
				<div className={searchField}>
					<InputSearch
						placeholder="Search model number"
						onChange={(event: TOnChangeInput) => setSearchTerm(event.target.value)}
					/>
				</div>
				{isSearching && (
					<span className={resultsHint} data-testid="search-results-hint">
						{matches ? `${matches} of ${total} model numbers` : "No model numbers found"}
					</span>
				)}
			</div>
			{collapseItems.length > 0 ? (
				<>
					<div className={tableHeader} data-testid="table-header">
						<span />
						{HEADER_COLUMNS.map((col) => (
							<span key={col.title} className={headerColumn} style={{ width: col.width }}>
								{col.title}
							</span>
						))}
					</div>
					{/* keyed so antd re-applies defaultActiveKey, which it only reads on mount */}
					<Collapse
						key={isSearching ? `search-${debouncedSearchTerm}` : "all"}
						items={collapseItems}
						defaultActiveKey={isSearching ? matchedTopLevelKeys : undefined}
					/>
				</>
			) : (
				<div className={noData}>{isSearching ? "No model numbers found" : "No data available"}</div>
			)}
		</div>
	)
}
